import { PublicClientApplication } from "@azure/msal-browser";
import * as microsoftTeams from "@microsoft/teams-js";
import { format } from "date-fns";
import {
  $gray600,
  $greenStatus,
  $redStatus,
  $yellowStatus,
} from "../../cssjs/colors";
import PageEnum, { BreadcrumbEnumYQB } from "../../enum/PageEnum";
import { ISTATUS, WithId } from "../../models/common/CommonModels";
import { getAirportConfiguration } from "./AirportConfiguration";
import {
  AIRPORT_CODE_PARAMETER,
  END_TIME,
  START_TIME,
  TIMEZONE,
} from "./Constants";
import {
  convertToGMTOffset,
  getGMTOffset,
  getTimeDifference,
  payloadDateFormat,
} from "./DateHelperFunctions";

export function getAirportConfig(airportCode: string): any {
  const airportConfigurationData = getAirportConfiguration();
  const airportConfiguration = Array.isArray(airportConfigurationData)
    ? airportConfigurationData.find(
        (config: { iataCode: string }) => config.iataCode === airportCode
      )
    : undefined;
  if (!airportConfiguration) {
    return {
      iataCode: "XYZ",
      icaoCode: "XYZA",
      name: "XYZ International Airport",
      timeZoneId: "Europe/London",
      location: [51.47, -0.4543],
    };
  }
  return airportConfiguration;
}

export function navigateToComponent(
  url: string,
  scrollId: string,
  entityId: string
) {
  if (scrollId) {
    window.sessionStorage.setItem("scrollId", scrollId);
  } else {
    window.sessionStorage.removeItem("scrollId");
  }

  if (window?.location?.ancestorOrigins?.length > 0) {
    const teamsAppId = process.env.REACT_APP_TEAMSAPP_ID;
    microsoftTeams.executeDeepLink(
      `https://teams.microsoft.com/l/entity/${teamsAppId}/${entityId}?webUrl=${url}`
    );
  } else {
    window.location.href = url;
  }
}

export function isInTeams() {
  // eslint-disable-next-line dot-notation
  const microsoftTeamsLib = microsoftTeams || (window as any)["microsoftTeams"];

  if (!microsoftTeamsLib) {
    return false; // the Microsoft Teams library is for some reason not loaded
  }

  if (
    (window.parent === window.self && (window as any).nativeInterface) ||
    window.name === "embedded-page-container" ||
    window.name === "extension-tab-frame"
  ) {
    return true;
  }
  return false;
}

export async function getLoggedInUser(
  msTeamsLib: any,
  msalInstance: PublicClientApplication
) {
  const isInTeamsContext = isInTeams();

  if (isInTeamsContext) {
    return new Promise((resolve, _reject) => {
      msTeamsLib.getContext((context: any) => {
        resolve(context.userPrincipalName);
      });
    });
  } else {
    return msalInstance.getActiveAccount()?.username;
  }
}

/**
 * This function is use to add time zone difference property wrt local time in each airport in the airport list
 * @param airportList - List of airports
 * @returns Updated airport list with time zone difference
 */
export function addTimeZoneDiff(airportList: any) {
  const gmtOffset = getGMTOffset(new Date());
  return airportList.map((airport: any) => {
    let timeZoneDiff;
    if (airport.timeZone === gmtOffset) {
      timeZoneDiff = null;
    } else {
      const { hours, minutes, sign } = getTimeDifference(
        gmtOffset,
        airport.timeZone
      );
      timeZoneDiff = { hours, minutes, sign };
    }
    return {
      ...airport,
      timeZoneDiff,
    };
  });
}
export function addTimeZoneDifference(airportList: any) {
  const gmtOffset = getGMTOffset(new Date());
  return airportList.map((airport: any) => {
    let timeZoneDiff;
    airport.timeZone = convertToGMTOffset(airport.timeZoneId);
    if (airport.timeZone === gmtOffset) {
      timeZoneDiff = null;
    } else {
      const { hours, minutes, sign } = getTimeDifference(
        gmtOffset,
        airport.timeZone
      );
      timeZoneDiff = { hours, minutes, sign };
    }
    return {
      ...airport,
      timeZoneDiff,
    };
  });
}

export function isValidTenant(
  userTenantId: string | undefined,
  allowedTenants: any
) {
  let isValid = false;
  if (userTenantId) {
    if (allowedTenants.includes(userTenantId)) {
      isValid = true;
    }
  }

  return isValid;
}

export async function getAirportCodeHelper(
  msalInstance: PublicClientApplication
): Promise<string> {
  const userEmail = (await getLoggedInUser(
    microsoftTeams,
    msalInstance
  )) as string;
  const stored = sessionStorage.getItem("airportCode");
  const airportConfigurations = getAirportConfiguration();
  const airportCode = stored ? stored : airportConfigurations[0]?.iataCode;
  return Promise.resolve(airportCode);
}

export function getLayoutClass(isInTeamsContext: boolean) {
  return isInTeamsContext ? "amd-maincontainer--teams" : "";
}

export const capitalizeValue = (detail?: string | null) => {
  return typeof detail === "string"
    ? detail.charAt(0).toUpperCase() + detail.slice(1)
    : detail;
};

/**
 * Finds the minimum value from an array of objects.
 *
 * @param {any[]} [data=[]] - The array of objects to search through. Each object should have a `value` property.
 * @returns {number} - The minimum value found in the array. Returns 0 if the array is empty.
 */
export const findMinValue = (data: any[] = []) => {
  return data.length === 0 ? 0 : Math.min(...data.map((item) => item.value));
};

/**
 * Returns a cyclic value within the specified limits.
 * If the input value exceeds the higher limit, it wraps around to the lower limit.
 * If the input value is below the lower limit, it wraps around to the higher limit.
 *
 * @param lowerLimit - The lower limit of the cyclic range.
 * @param higherLimit - The higher limit of the cyclic range.
 * @param value - The input value to be cycled.
 * @returns The cyclic value within the specified limits.
 */
export const getCyclicInputValue = (
  lowerLimit: number,
  higherLimit: number,
  value: number
) => {
  let result = value;
  if (value > higherLimit) {
    result = lowerLimit;
  } else if (value < lowerLimit) {
    result = higherLimit;
  }
  return result;
};

/**
 * Constructs an API URL by replacing placeholders in the given URL template
 * with the provided parameters.
 *
 * @param apiUrl - The API URL template containing placeholders such as
 *                 `{airportCode}`, `{startDateTime}`, `{endDateTime}`, and `{timezone}`.
 *                 Defaults to an empty string if not provided.
 * @param airport - The airport code to replace the `{airportCode}` placeholder.
 * @param startDate - The start date to replace the `{startDateTime}` placeholder.
 * @param endDate - The end date to replace the `{endDateTime}` placeholder.
 * @param timezone - The timezone to replace the `{timezone}` placeholder. Defaults to an LT if not provided.
 * @returns The formatted API URL with all placeholders replaced by the provided values.
 */
export const getApiUrl = (
  apiUrl: string | undefined,
  airport: string,
  startDate: Date,
  endDate: Date,
  timezone = "LT"
) => {
  if (!apiUrl) {
    return "";
  }
  return apiUrl
    .replace(AIRPORT_CODE_PARAMETER, airport)
    .replace(START_TIME, format(startDate, payloadDateFormat))
    .replace(END_TIME, format(endDate, payloadDateFormat))
    .replace(TIMEZONE, timezone);
};

/**
 * Generates a list of breadcrumb pages which can be navigated based on the provided airport code.
 *
 * @param airport - The airport code used to determine the breadcrumb pages.
 *                  If the airport code is "YQB", a specific set of breadcrumb pages
 *                  is returned. For other airport codes, a default set of breadcrumb
 *                  pages is returned.
 * @returns An array of breadcrumb page objects, where each object contains:
 *          - `key`: A unique key representing the page URL.
 *          - `text`: The display text for the breadcrumb.
 *          - `url`: The URL of the breadcrumb page.
 */
export const getBreadcrumbPageList = (airport: string) => {
  if (!airport) {
    return [];
  }
  const breadcrumbEnum =
    airport === "YQB" ? BreadcrumbEnumYQB : PageEnum.BreadcrumbEnum;

  return Object.keys(breadcrumbEnum).map((key) => {
    const pageKey = key as keyof typeof PageEnum.PageUrl;
    return {
      key: PageEnum.PageUrl[pageKey],
      text: breadcrumbEnum[key as keyof typeof breadcrumbEnum],
      url: `/${PageEnum.PageUrl[pageKey]}`,
    };
  });
};

/**
 * Removes duplicate objects from an array based on their `id` property.
 *
 * This function takes an array of objects that extend the `WithId` interface,
 * filters out objects with duplicate `id` values, and returns a new array
 * containing only unique objects.
 *
 * @typeParam T - A type that extends the `WithId` interface, ensuring that
 *                each object in the array has an `id` property.
 * @param data - The array of objects to be filtered for duplicates.
 * @returns A new array containing only objects with unique `id` values.
 */
export const removeIdDuplicates = <T extends WithId>(data: T[]): T[] => {
  const seen = new Set<string>();
  return data.filter((item: any) => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
};

/**
 * Scrolls the page to the top by targeting the element with the ID "back-to-top-anchor".
 *
 * This function locates the DOM element with the ID "back-to-top-anchor" and smoothly scrolls
 * the page to the top of that element. If the element is not found, the function does nothing.
 */
export const scrollToTop = () => {
  const element = document.getElementById("back-to-top-anchor");

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

/**
 * Pixel to rem converter
 */
export const convertPxToRem = (px: number, baseUnit: number = 14): string =>
  `${px / baseUnit}rem`;

/**
 * This function returns the appropriate CSS class names based on the provided status.
 */
export const getStylesForStatus = (
  status: ISTATUS | undefined,
  classes: {
    cellInner: string;
    cellModerate: string;
    cellPoor: string;
  }
): string => {
  switch (status) {
    case "moderate":
      return `${classes.cellInner} ${classes.cellModerate}`;
    case "poor":
      return `${classes.cellInner} ${classes.cellPoor}`;
    default:
      // Ignore others
      return classes.cellInner;
  }
};

export const filledColorSelection = (status: ISTATUS | undefined) => {
  switch (status) {
    case "moderate":
      return $yellowStatus;
    case "poor":
      return $redStatus;
    case "good":
      return $greenStatus;
    default:
      return $gray600;
  }
};

export const getTextAlignment = (align?: "center" | "left" | "right") => {
  switch (align) {
    case "center":
      return { textAlign: "center" as const };
    case "right":
      return { textAlign: "right" as const };
    case "left":
      return { textAlign: "left" as const };
    default:
      return undefined;
  }
};

export function ensureArray<T>(value?: T[] | null): T[] {
  return Array.isArray(value) && value.length > 0 ? [...value] : [];
}

export const capitalizeFirst = (str = "") =>
  str ? str[0].toUpperCase() + str.slice(1) : "";

export const toSentenceCase = (str?: string | null): string => {
  if (!str) {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const joinCodes = (codes: string[]): string | null =>
  codes.length > 0 ? codes.join(", ") : null;

/**
 * Validates that a URL is a safe internal route.
 * Rejects absolute external URLs, protocol-relative URLs, and dangerous schemes.
 * Only allows relative paths starting with "/".
 *
 * @param url - The URL string to validate.
 * @returns `true` if the URL is a safe internal path, `false` otherwise.
 */
export function isValidInternalUrl(url: string): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  const trimmedUrl = url.trim().toLowerCase();

  // Strip all whitespace and control characters to defeat obfuscation (e.g. "java\tscript:")
  if (
    /^javascript:/i.test(trimmedUrl) ||
    /^data:/i.test(trimmedUrl) ||
    /^vbscript:/i.test(trimmedUrl)
  ) {
    return false;
  }

  // Reject protocol-relative URLs (e.g., "//evil.com")
  if (trimmedUrl.startsWith("//")) {
    return false;
  }

  // Reject absolute external URLs (http:// or https://)
  if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
    return false;
  }

  // Must be a relative internal path starting with "/"
  return url.startsWith("/");
}

/**
 * Rounds a numeric value to the nearest integer using standard rounding rules.
 * Returns the original value unchanged if it is not a valid number.
 * - Decimal >= 0.5 → rounds up
 * - Decimal < 0.5 → rounds down
 */
export const roundValue = (value: any): number | string => {
  const num = Number(value);
  return isNaN(num) ? value : Math.round(num);
};
